import { useCallback, useEffect, useRef, useState } from 'react';
import {
  cancelMiniLogin, clearPendingMiniLogin, consumeMiniLogin, createMiniLogin, miniLoginConfig,
  MiniLoginError, MiniLoginMode, MiniLoginStatus, openMiniProgram, PendingMiniLogin,
  readMiniLogin, restorePendingMiniLogin, savePendingMiniLogin,
} from '../services/miniProgramLogin';

interface Options {
  eligible: boolean;
  authenticated: boolean;
  active: boolean;
  agreementAccepted: boolean;
  onResume: () => void;
  onSuccess: () => void;
}

export function useMiniProgramLogin(options: Options) {
  const latest = useRef(options);
  latest.current = options;
  const [enabled, setEnabled] = useState(false);
  const [session, setSession] = useState<PendingMiniLogin | null>(null);
  const [status, setStatus] = useState<MiniLoginStatus>('PENDING');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pending = useRef<PendingMiniLogin | null>(null);
  const generation = useRef(0);
  const mounted = useRef(true);

  const cancel = useCallback(() => {
    generation.current++;
    const old = pending.current;
    pending.current = null;
    clearPendingMiniLogin();
    setSession(null);
    setBusy(false);
    setError('');
    setStatus('PENDING');
    if (old) void cancelMiniLogin(old.sessionId).catch(() => undefined);
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!options.eligible) return;
    let disposed = false;
    miniLoginConfig().then(config => {
      if (disposed) return;
      setEnabled(config.enabled);
      const saved = restorePendingMiniLogin();
      if (saved && !latest.current.authenticated) {
        pending.current = saved;
        setSession(saved);
        if (config.enabled) latest.current.onResume();
      }
    }).catch(() => { if (!disposed) setEnabled(false); });
    return () => { disposed = true; };
  }, [options.eligible]);

  useEffect(() => {
    if (options.authenticated || !options.eligible) cancel();
    if (!options.eligible) setEnabled(false);
  }, [options.authenticated, options.eligible, cancel]);

  const start = useCallback(async (mode: MiniLoginMode) => {
    if (!latest.current.eligible || !latest.current.agreementAccepted || latest.current.authenticated) return;
    cancel();
    const current = generation.current;
    setBusy(true);
    try {
      const result = await createMiniLogin(mode);
      if (!mounted.current || current !== generation.current || !latest.current.active) {
        void cancelMiniLogin(result.sessionId).catch(() => undefined);
        return;
      }
      const next: PendingMiniLogin = { ...result, mode,
        returnTo: window.location.pathname + window.location.search + window.location.hash };
      pending.current = next;
      try { savePendingMiniLogin(next); } catch {
        pending.current = null;
        void cancelMiniLogin(next.sessionId).catch(() => undefined);
        throw new MiniLoginError('storageUnavailable');
      }
      setSession(next);
      setStatus(next.status);
      if (mode === 'link' && next.miniProgramUrlLink) openMiniProgram(next.miniProgramUrlLink);
    } catch (failure) {
      if (mounted.current && current === generation.current) {
        setError(failure instanceof MiniLoginError ? failure.code : 'unavailable');
      }
    } finally {
      if (mounted.current && current === generation.current) setBusy(false);
    }
  }, [cancel]);

  useEffect(() => {
    // Hiding the entry does not interrupt a request already confirmed in the mini-program.
    if (!session || options.authenticated) return;
    let disposed = false;
    let inFlight = false;
    let stopped = false;
    const current = generation.current;
    const valid = () => !disposed && !stopped && current === generation.current && mounted.current;
    const poll = async () => {
      if (!valid() || inFlight || document.visibilityState === 'hidden') return;
      inFlight = true;
      try {
        const result = await readMiniLogin(session.sessionId);
        if (!valid()) return;
        setStatus(result.status);
        setError('');
        if (result.status === 'CONFIRMED' || result.status === 'CONSUMED') {
          if (!latest.current.agreementAccepted || latest.current.authenticated) return;
          await consumeMiniLogin(session.sessionId);
          if (!valid()) return;
          stopped = true;
          pending.current = null;
          clearPendingMiniLogin();
          setSession(null);
          latest.current.onSuccess();
        } else if (result.status === 'EXPIRED' || result.status === 'CANCELLED') {
          stopped = true;
          clearPendingMiniLogin();
        }
      } catch (failure) {
        if (!valid()) return;
        const code = failure instanceof MiniLoginError ? failure.code : 'unavailable';
        setError(code);
        if (['disabled', 'invalidSession', 'invalidBrowser', 'invalidOrigin', 'alreadyLoggedIn'].includes(code)
            || Date.now() > session.expiresAt + 60000) {
          stopped = true;
          clearPendingMiniLogin();
          if (code === 'alreadyLoggedIn') { pending.current = null; setSession(null); latest.current.onSuccess(); }
        }
      } finally { inFlight = false; }
    };
    void poll();
    const timer = window.setInterval(poll, 2000);
    window.addEventListener('pageshow', poll);
    window.addEventListener('focus', poll);
    document.addEventListener('visibilitychange', poll);
    return () => {
      disposed = true;
      window.clearInterval(timer);
      window.removeEventListener('pageshow', poll);
      window.removeEventListener('focus', poll);
      document.removeEventListener('visibilitychange', poll);
    };
  }, [session, options.active, options.authenticated]);

  return { enabled, session, status, busy, error, start, cancel };
}
